import React from 'react';
import { Play, Star, ExternalLink, MessageCircle } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import useReveal from '../../hooks/useReveal';

export default function InPlay({ onEnquire }) {
  const { data } = useSite();
  const { query, matches: filterMatches } = useFilter();
  const matches = data.inPlayMatches || [];

  const q = (query || '').trim().toLowerCase();
  const filteredMatches = q
    ? matches.filter(filterMatches)
    : matches;

  return (
    <div id="get-id" className="rounded-xl bg-zinc-950/40 border border-zinc-900 overflow-hidden" data-testid="get-id-section">
      <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-[color:var(--gold)]/15 to-transparent border-b border-zinc-900">
        <span className="w-6 h-6 rounded-full bg-[color:var(--gold)] text-black flex items-center justify-center">
          <Play size={12} fill="currentColor" />
        </span>
        <span className="text-white font-bold text-sm md:text-base">GET ID</span>
      </div>

      <div className="hidden md:grid grid-cols-12 items-center px-4 py-2.5 border-b border-zinc-900 bg-zinc-950/60 text-xs font-semibold text-zinc-400">
        <div className="col-span-5">Platform</div>
        <div className="col-span-3">Type</div>
        <div className="col-span-2 text-center">Rating</div>
        <div className="col-span-2 text-right">Action</div>
      </div>

      <div className="divide-y divide-zinc-900">
        {filteredMatches.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-xs">
            No platforms matching "{query}"
          </div>
        ) : (
          filteredMatches.map((m, i) => <Row key={m.id} m={m} delay={i * 60} onEnquire={() => onEnquire?.(null, { name: m.name, id: m.id })} />)
        )}
      </div>
    </div>
  );
}

function Row({ m, delay, onEnquire }) {
  const { ref, visible } = useReveal();
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }}
      className={`match-row grid grid-cols-12 items-center px-3 md:px-4 py-3 gap-2 transition-all duration-500
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
      <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-md bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
          {m.logo ? <img src={m.logo} alt={m.name} className="max-w-full max-h-full object-contain" /> : <span className="text-[color:var(--gold)] font-bold text-sm">{(m.name || 'P').charAt(0).toUpperCase()}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-white font-semibold text-sm truncate">{m.name}</div>
            {m.rating && (
              <div className="md:hidden inline-flex items-center gap-1 shrink-0">
                <Star size={11} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
                <span className="text-white text-xs font-bold">{m.rating}</span>
              </div>
            )}
          </div>
          <div className="text-zinc-400 text-[11px] truncate">{m.type ? `${m.type} · ` : ''}{m.tags}</div>
        </div>
        <div className="md:hidden flex items-center gap-1.5 shrink-0">
          <a
            href={m.visitUrl || m.link || 'https://lotusdemo1.com/'}
            target="_blank"
            rel="noreferrer"
            className="h-8 px-3 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black text-[11px] font-bold flex items-center justify-center gap-1"
          >
            <ExternalLink size={11} /> Visit
          </a>
        </div>
      </div>
      <div className="hidden md:block col-span-3 text-zinc-300 text-xs font-medium truncate">{m.type}</div>
      <div className="hidden md:flex col-span-2 items-center justify-center gap-1">
        <Star size={12} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
        <span className="text-white text-sm font-bold">{m.rating}</span>
        {m.reviews && <span className="text-zinc-500 text-[10px]">({m.reviews})</span>}
      </div>
      <div className="hidden md:flex col-span-2 items-center justify-end gap-1.5">
        <a
          href={m.visitUrl || m.link || 'https://lotusdemo1.com/'}
          target="_blank"
          rel="noreferrer"
          className="h-8 px-3 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black text-[11px] font-bold flex items-center justify-center gap-1"
        >
          <ExternalLink size={12} /> Visit Site
        </a>
      </div>
    </div>
  );
}
